import {
  MediaPlayer,
  MediaPlayerInstance,
  MediaProvider,
  Poster,
  Track,
  useMediaState,
} from "@vidstack/react";
import {
  defaultLayoutIcons,
  DefaultVideoLayout,
} from "@vidstack/react/player/layouts/default";

import { useEffect, useRef, useState } from "react";
import "@vidstack/react/player/styles/default/theme.css";
import "@vidstack/react/player/styles/default/layouts/video.css";
import { ReactSVG } from "react-svg";
import { useGlobalContext } from "@aleksundshantu.com/aunds-utils-react/contexts";
import { FileType, ImageType } from "@/types/baseTypes";
import { useRouter } from "next/router";
import { AudioIcon, PlayPauseButton } from "./Video";
import { CustomLink } from "../CustomLink";
import styles from "./Video.module.scss";
import { optimizeURL } from "@/utils/url";
import useWindowSize from "@/hooks/useWindowSize";
import { HeaderVideosType } from "@/types/PageType";
import Image from "next/image";
import { iframeSrcFetch } from "@/utils/iframeSrcFetch";
import { has } from "lodash";

interface VidstackPlayerProps {
  controls?: boolean;
  autoplay: boolean;
  loop?: boolean;
  muted: boolean;
  video_decorative?: boolean;
  singlePage?: boolean;
  singlePageUrl?: string;
  video: HeaderVideosType;
  video_audio_description?: FileType | false;
  headerVideo?: boolean;
  sizes?: string;
}

const VidstackPlayer = ({
  video,
  autoplay,
  loop,
  muted: mutedProps,
  video_decorative = false,
  singlePage = false,
  singlePageUrl,
  video_audio_description,
  headerVideo,
  sizes = "100vw",
}: VidstackPlayerProps) => {
  const {
    internes_video_desktop: video_desktop,
    internes_video_tablet: video_tablet,
    internes_video_mobile: video_mobile,
    internes_video_fullhd: video_fullhd,
    video_thumb_fullhd,
    video_thumbnail_desktop,
    video_thumb_tablet,
    video_thumb_mobile,
    audio,
    externes_video,
    video_type,
  } = video;
  const { getIcon, getLabel } = useGlobalContext();
  const router = useRouter();
  const player = useRef<MediaPlayerInstance>(null);
  const isMuted = useMediaState("muted", player);
  const [showControls, setShowControls] = useState(
    autoplay && !video_decorative,
  );
  const showPlayBtn = !showControls;
  const [activeVideo, setActiveVideo] = useState<FileType | false>(
    video_audio_description ? video_audio_description : video_desktop,
  );
  const [videoThumb, setVideoThumb] = useState<ImageType | false>(
    video_thumbnail_desktop,
  );
  const { width } = useWindowSize();

  // responsive video source selection
  // TODO: maybe find a beeter approach without using useWindowSize & useEffect
  useEffect(() => {
    if (
      !width ||
      video_audio_description ||
      singlePage ||
      video_type !== "upload"
    )
      return;
    if (video_mobile && width < 768) {
      setActiveVideo(video_mobile);
      if (video_thumb_mobile) setVideoThumb(video_thumb_mobile);
    } else if (video_tablet && width < 1023) {
      setActiveVideo(video_tablet);
      if (video_thumb_tablet) setVideoThumb(video_thumb_tablet);
    } else if (video_desktop && width < 1408) {
      setActiveVideo(video_desktop);
      if (video_thumbnail_desktop) setVideoThumb(video_thumbnail_desktop);
    } else if (video_fullhd && width >= 1408) {
      setActiveVideo(video_fullhd);
      if (video_thumb_fullhd) setVideoThumb(video_thumb_fullhd);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [width]);

  const [imageURL, setImageURL] = useState("");
  const [videoTitle, setVideoTitle] = useState<string>("");

  // fetch thumbnail for externes_video
  useEffect(() => {
    if (!video || video_type === "upload" || !externes_video) return;
    async function fetchThumbnail() {
      if (video_thumbnail_desktop && has(video_thumbnail_desktop, "url")) {
        setImageURL(video_thumbnail_desktop.url);
        return;
      }

      // getVideoTitle
      const titleRegex = /title="([^"]*)"/;
      const match = externes_video?.match(titleRegex);
      if (match && match[1]) {
        const title = match[1];
        const decodedTitle = new DOMParser().parseFromString(title, "text/html")
          .documentElement.textContent;
        setVideoTitle(decodedTitle || title);
      }

      // fetch video thumbnail
      const srcUrl = externes_video?.match(/src="([^"]+)"/);

      if (srcUrl) {
        const src = srcUrl[1];

        const videoThumbnailDataUrl = await fetch(
          `/api/fetchVideoThumbnail?url=${src}`,
        )
          .then((res) => {
            if (!res.ok) {
              throw new Error("Network response was not ok");
            }
            return res.json();
          })
          .then((data) => {
            const dataURL = data.videoThumbnail.dataURL;
            return dataURL;
          })
          .catch((error) => {
            console.error("Error:", error);
          });

        if (videoThumbnailDataUrl) {
          setImageURL(videoThumbnailDataUrl);
        }
      }
    }
    if (
      (video_type === "Vimeo" && externes_video) ||
      (video_type === "Youtube" && externes_video)
    )
      fetchThumbnail();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [video]);

  const InfoButton = ({ className }: { className: string }) => {
    return (
      singlePageUrl && (
        <CustomLink
          className={`${className ? className : ""}`}
          href={optimizeURL(singlePageUrl)}
          target={"_blank"}
          {...(activeVideo && {
            "aria-label": getLabel(
              "aria_label_video_player_info_button",
            ).replace("$$title", activeVideo.title),
          })}
        >
          <ReactSVG
            width={32}
            height={32}
            src={getIcon("video-controls-transcript")}
            wrapper="svg"
            beforeInjection={(svg) => {
              svg.setAttribute("alt", "transcript Icon");
              svg.setAttribute("aria-label", "transcript Icon");
              svg.setAttribute("aria-hidden", "true");
            }}
            aria-hidden
            className="vds-icon"
          />
        </CustomLink>
      )
    );
  };

  const playerComponents = (
    <>
      {singlePage && video?.captions && (
        <Track
          kind="captions"
          src={video?.captions.url}
          lang={router.locale ?? router.defaultLocale ?? "de"}
          label={video.description}
        />
      )}
      <MediaProvider />

      {showControls && (
        <DefaultVideoLayout
          icons={defaultLayoutIcons}
          slots={{
            ...(singlePageUrl &&
              !singlePage && {
                afterSettingsMenu: (
                  <InfoButton
                    className={`${styles["details-btn"]} vds-button`}
                  />
                ),
              }),
          }}
        />
      )}
    </>
  );

  return (
    <div
      className={`${styles["video-player-wrapper"]}`}
      data-video-player-single-page={singlePage ? "true" : "false"}
    >
      {/* Upload video */}
      {video_type === "upload" && activeVideo && (
        /* jscpd:ignore-start */
        <MediaPlayer
          src={activeVideo.url}
          viewType="video"
          streamType="on-demand"
          crossOrigin
          playsInline
          autoPlay={autoplay}
          muted={autoplay ? true : mutedProps}
          loop={loop}
          aria-label={activeVideo.alt}
          ref={player}
          aspectRatio="unset"
          title={activeVideo.title}
        >
          {videoThumb && videoThumb.url && (
            <Poster className="vds-poster" asChild>
              <Image
                src={videoThumb.url}
                alt={videoThumb.alt}
                width={videoThumb.width}
                height={videoThumb.height}
                priority={headerVideo}
                sizes={sizes}
              />
            </Poster>
          )}
          {playerComponents}
        </MediaPlayer>
      )}

      {/* Externes video: Vimeo & Youtube */}
      {video_type !== "upload" && externes_video && (
        <MediaPlayer
          src={iframeSrcFetch(externes_video)}
          viewType="video"
          streamType="on-demand"
          crossOrigin
          playsInline
          autoPlay={autoplay}
          ref={player}
          aria-label={videoTitle}
        >
          {imageURL && (
            <Poster className="vds-poster" asChild>
              <Image
                src={imageURL}
                alt={`${getLabel("thumbnail")} ${videoTitle}`}
                width={0}
                height={0}
                style={{ width: "100%", height: "auto" }}
                sizes={sizes}
              />
            </Poster>
          )}
          {playerComponents}
        </MediaPlayer>
      )}

      {audio && !showControls && (
        <AudioIcon isMuted={isMuted} player={player} getIcon={getIcon} />
      )}
      {!showControls && video_decorative && singlePageUrl && (
        <InfoButton className={styles["details-btn-decorative"]} />
      )}
      {showPlayBtn && (
        <PlayPauseButton
          isPlaying={autoplay}
          player={player}
          getIcon={getIcon}
          setShowcontrols={setShowControls}
          video_decorative={video_decorative}
        />
      )}
    </div>
  );
};

export default VidstackPlayer;
